import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "@/motion/appMotion";
import {
  features as staticFeatures,
  mainFeaturesContent,
} from "../../../data/landingPage/mainFeaturesContent";
import {
  LANDING_CONTAINER_PADDED,
  LANDING_INNER,
} from "../../../constants/landingPageLayout";
import FeatureIcon from "../../common/FeatureIcon";
import useLandingMotion from "../../../hooks/useLandingMotion";
import { fetchPublicFeatures } from "../../../utils/featuresApi";

const LANDING_FEATURE_COUNT = 8;

const STATIC_FEATURE_ICON_NAMES = {
  "multi-source job fetching": "cube",
  "automated job matching": "magnifying-glass",
  "usage analytics": "chart-bar",
  "team collaboration": "user-group",
  "candidate database": "circle-stack",
  "job alerts": "bell-alert",
  "smart filters & tags": "tag",
  "export to excel/csv": "document-arrow-down",
};

function getFeatureIconName(feature, fromApi) {
  if (fromApi && feature.icon_name) return feature.icon_name;
  return STATIC_FEATURE_ICON_NAMES[String(feature.title || "").toLowerCase()] || "sparkles";
}

function MainFeatureCardSkeleton({ index, getProps }) {
  return (
    <div className="h-full w-full rounded-[20px] border border-[#E2E8F0]">
      <motion.div
        {...getProps({ variant: "fade", delay: index * 0.1 })}
        className="flex min-h-[220px] w-full flex-col justify-center gap-3 rounded-[20px] bg-white px-6 py-5 animate-pulse sm:min-h-[240px]"
      >
        <div className="flex w-full flex-col gap-2.5">
          <div className="h-7 w-7 shrink-0 rounded-md bg-slate-200" />
          <div className="h-5 w-3/4 rounded bg-slate-200" />
        </div>
        <div className="flex w-full flex-col gap-2">
          <div className="h-3.5 w-full rounded bg-slate-100" />
          <div className="h-3.5 w-5/6 rounded bg-slate-100" />
        </div>
      </motion.div>
    </div>
  );
}

function MainFeatureCard({ feature, index, getProps, fromApi }) {
  const title = feature.title;
  const description = fromApi
    ? feature.short_description
    : feature.description;
  const slug = fromApi ? feature.slug : null;
  const iconName = getFeatureIconName(feature, fromApi);

  const cardInner = (
    <motion.div
      {...getProps({ variant: "fade", delay: index * 0.1 })}
      className="flex h-full min-h-[220px] w-full flex-col justify-center gap-[10px] rounded-[20px] bg-white px-6 py-5 transition-all duration-300 sm:min-h-[240px]"
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center text-[#4F46E5] [&_svg]:!h-7 [&_svg]:!w-7 [&_svg]:!text-[#4F46E5]">
        <FeatureIcon
          iconName={iconName}
          variant="solid"
          className="h-7 w-7 text-[#4F46E5]"
        />
      </div>

      <h3
        className="text-[18px] font-semibold leading-[1.3] tracking-tight text-[#0F172A] sm:text-[20px]"
        style={{ fontFamily: '"Rethink Sans", sans-serif' }}
      >
        {title}
      </h3>

      <p
        className="text-[14px] leading-[1.5] text-[#64748B] sm:text-[15px]"
        style={{ fontFamily: '"Inter", sans-serif' }}
      >
        {description}
      </p>
    </motion.div>
  );

  const cardBorder = (
    <div className="group/card h-full w-full rounded-[20px] border border-[#E2E8F0] transition-all duration-300 hover:-translate-y-1 hover:border-[#4F46E5]/50 hover:shadow-md group-hover/link:-translate-y-1 group-hover/link:border-[#4F46E5]/50 group-hover/link:shadow-md">
      {cardInner}
    </div>
  );

  if (slug) {
    return (
      <Link
        to={`/features/${slug}`}
        className="group/link block h-full rounded-[20px] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
      >
        {cardBorder}
      </Link>
    );
  }

  return cardBorder;
}

const MainFeatures = () => {
  const { getProps } = useLandingMotion();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromApi, setFromApi] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchPublicFeatures();
        if (cancelled) return;

        const list = Array.isArray(data) ? data.slice(0, LANDING_FEATURE_COUNT) : [];
        if (list.length > 0) {
          setFeatures(list);
          setFromApi(true);
        } else {
          setFeatures(staticFeatures.slice(0, LANDING_FEATURE_COUNT));
          setFromApi(false);
        }
      } catch {
        if (cancelled) return;
        setFeatures(staticFeatures.slice(0, LANDING_FEATURE_COUNT));
        setFromApi(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="features"
      className="relative w-full py-10 sm:py-12 lg:py-14 clip-x-visible-y"
      style={{ background: "rgba(241, 245, 249, 0.5)" }}
    >
      <div className={LANDING_CONTAINER_PADDED}>
        <div className={`${LANDING_INNER} flex flex-col gap-8 py-5 sm:gap-10 sm:py-8 md:py-10 lg:gap-12 lg:py-[45px]`}>
            <motion.div
              {...getProps({ variant: "fadeUp", duration: 0.6 })}
              className="flex w-full max-w-[800px] mx-auto flex-col gap-4 text-center items-center justify-center mb-4"
            >
              <h2
                className="text-[clamp(32px,4.5vw,44px)] font-bold leading-[1.2] tracking-tight text-[#0F172A]"
                style={{ fontFamily: '"Rethink Sans", sans-serif' }}
              >
                Everything you need to <br className="hidden sm:block" />
                <span
                  style={{
                    backgroundImage:
                      "linear-gradient(180deg, #7D3BEE 0%, #4F46E5 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  streamline your job sourcing
                </span>
              </h2>
              <p
                className="text-base leading-[1.5] text-[#64748B] sm:text-[16px] md:text-[18px] max-w-[650px]"
                style={{ fontFamily: '"Inter", sans-serif' }}
              >
                Designed specifically for the unique needs of Indian job consultancy firms
              </p>
            </motion.div>

            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4 lg:gap-6">
              {loading
                ? Array.from({ length: LANDING_FEATURE_COUNT }).map((_, index) => (
                    <MainFeatureCardSkeleton
                      key={`main-feature-skeleton-${index}`}
                      index={index}
                      getProps={getProps}
                    />
                  ))
                : features.map((feature, index) => (
                    <MainFeatureCard
                      key={feature.slug || feature.id || feature.title}
                      feature={feature}
                      index={index}
                      getProps={getProps}
                      fromApi={fromApi}
                    />
                  ))}
            </div>
        </div>
      </div>
    </section>
  );
};

export default MainFeatures;
